import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';

const INSERT_DOCUMENTO = gql`
  mutation InsertDocumento($object: documentos_insert_input!) {
    insert_documentos_one(object: $object) {
      id
      nombre
      contenido
      fecha
    }
  }
`;

const GET_PLANTILLAS = gql`
  query GetPlantillas {
    plantillas(order_by: { nombre: asc }) {
      id
      nombre
      plantilla
      plantilla_campos(order_by: { orden: asc }) {
        id
        key
        label
        type
        placeholder
        orden
      }
    }
  }
`;

const buildAccusadoContext = (acusado) => {
  const nombre = acusado?.nombre || acusado?.nombre_completo || 'Acusado';
  return {
    acusado: nombre,
    acusado_nombre: nombre,
    acusado_grado: acusado?.grado || '',
    acusado_unidad: acusado?.unidad || '',
  };
};

const renderPlantilla = (base, values = {}, acusadoCtx = {}) => {
  let out = base || '';
  const dict = {
    ...values,
    ...buildAccusadoContext(acusadoCtx),
  };
  Object.keys(dict).forEach(k => {
    const val = dict[k] ?? '';
    out = out.replaceAll(`{{${k}}}`, String(val));
  });
  return out;
};

export default function DocumentosFormularios({ acusado, onDocumentoCreado }) {
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaId, setPlantillaId] = useState('');
  const [valores, setValores] = useState({});
  const [preview, setPreview] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [insertDocumento] = useMutation(INSERT_DOCUMENTO);
  const { data, loading, error } = useQuery(GET_PLANTILLAS);

  useEffect(() => {
    setPlantillas(data?.plantillas || []);
  }, [data]);

  const plantillaSel = useMemo(() => plantillas.find(p => p.id === plantillaId), [plantillas, plantillaId]);

  useEffect(() => {
    if (plantillaSel) {
      // Inicializa valores por defecto
      const init = {};
      plantillaSel.plantilla_campos.forEach(c => {
        if (c.type === 'date') init[c.key] = new Date().toISOString().slice(0,10);
        else init[c.key] = '';
      });
      setValores(init);
      setPreview(renderPlantilla(plantillaSel.plantilla, init, acusado));
    } else {
      setValores({});
      setPreview('');
    }
  }, [plantillaSel, acusado]);

  const onChangeCampo = (key, value) => {
    const next = { ...valores, [key]: value };
    setValores(next);
    if (plantillaSel) setPreview(renderPlantilla(plantillaSel.plantilla, next, acusado));
  };

  const handleCrear = async () => {
    if (!plantillaSel) return;
    setGuardando(true);
    try {
      const contenido = renderPlantilla(plantillaSel.plantilla, valores, acusado);
      await insertDocumento({
        variables: {
          object: {
            acusado_id: acusado.acusado_id,
            nombre: plantillaSel.nombre,
            contenido,
          },
        },
      });
      onDocumentoCreado && onDocumentoCreado();
    } finally {
      setGuardando(false);
    }
  };

  const handleImprimir = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Documento</title><meta charset="utf-8"/><style>body{font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial; padding:24px; line-height:1.5} pre{white-space:pre-wrap}</style></head><body><pre>${preview}</pre></body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const handleCopiar = async () => {
    if (!preview) return;
    try {
      await navigator.clipboard.writeText(preview);
      alert('Contenido copiado al portapapeles');
    } catch (e) {
      console.error(e);
    }
  };

  const handleDescargar = () => {
    if (!preview) return;
    const blob = new Blob([preview], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (plantillaSel?.nombre || 'documento') + '.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {loading && (
        <div className="text-center mt-2">
          <div className="spinner-border text-primary"></div>
        </div>
      )}
      {error && (
        <div className="alert alert-danger small">No se pudieron cargar las plantillas.</div>
      )}
      <div className="mb-3">
        <label className="form-label">Plantilla</label>
        <select className="form-select" value={plantillaId} onChange={e => setPlantillaId(e.target.value)}>
          <option value="">Selecciona una plantilla</option>
          {plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
      </div>

      {plantillaSel && (
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="card">
              <div className="card-header">Formulario</div>
              <div className="card-body">
                <form className="row g-2">
                  {plantillaSel.plantilla_campos.map(c => (
                    <div className="col-12" key={c.key}>
                      <label className="form-label">{c.label}</label>
                      {c.type === 'textarea' ? (
                        <textarea className="form-control" rows={4} placeholder={c.placeholder || ''} value={valores[c.key] || ''} onChange={e => onChangeCampo(c.key, e.target.value)} />
                      ) : (
                        <input className="form-control" type={c.type} placeholder={c.placeholder || ''} value={valores[c.key] || ''} onChange={e => onChangeCampo(c.key, e.target.value)} />
                      )}
                    </div>
                  ))}
                </form>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <button className="btn btn-primary" disabled={guardando} onClick={handleCrear}>Guardar documento</button>
                  <button className="btn btn-outline-secondary" type="button" onClick={handleImprimir}>Imprimir</button>
                  <button className="btn btn-outline-primary" type="button" onClick={handleCopiar}>Copiar</button>
                  <button className="btn btn-outline-success" type="button" onClick={handleDescargar}>Descargar .txt</button>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <div className="card">
              <div className="card-header">Vista previa</div>
              <div className="card-body">
                <pre className="bg-light p-2 small" style={{ minHeight: 160 }}>{preview || 'Selecciona una plantilla para ver la vista previa.'}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
